import React, { useEffect, useMemo } from 'react';
import { Field, FieldArray, useFormikContext } from 'formik';
import _ from 'lodash';

import { ApplicationModel, PhoneModel, PhoneType } from '../../types';
import { BaseFieldProps, Text, AddIcon } from '@tradingblock/components';
import { PhoneField } from './';

interface PhonesArrayFieldProps extends BaseFieldProps {
  id: 'primaryAccountHolder' | 'secondaryAccountHolder';
};

export const PhonesArrayField: React.FC<PhonesArrayFieldProps> = ({ id }) => {
  const { values, setFieldValue }  = useFormikContext<ApplicationModel>();

  const prefix = id ? `${id}.` : '';

  const phoneDataFromForm = values[id] && values[id].phones;

  const firstPhoneNumDataWithMobileType = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Mobile)) || null, [phoneDataFromForm]);
  const firstPhoneNumDataWithHomeType = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Home)) || null, [phoneDataFromForm]);
  const firstPhoneNumDataWithWorkType = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Work)) || null, [phoneDataFromForm]);
  const firstPhoneNumDataWithMobileTypeAndNum = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Mobile && phoneData.phoneNumber)) || null, [phoneDataFromForm]);
  const firstPhoneNumDataWithHomeTypeAndNum = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Home && phoneData.phoneNumber)) || null, [phoneDataFromForm]);
  const firstPhoneNumDataWithWorkTypeAndNum = useMemo(() => (phoneDataFromForm && phoneDataFromForm.find((phoneData) => phoneData.type === PhoneType.Work && phoneData.phoneNumber)) || null, [phoneDataFromForm]);

  // Cleanup old data from previously saved applications. We will take the first of each phone type as long as there is an element in the list
  // of phones with a type or a type and a number.
  useEffect(() => {
    const updatedFormPhoneData: PhoneModel[] = [];

    if (firstPhoneNumDataWithMobileTypeAndNum) {
      updatedFormPhoneData.push(firstPhoneNumDataWithMobileTypeAndNum);
    } else {
      if (firstPhoneNumDataWithMobileType) {
        updatedFormPhoneData.push(firstPhoneNumDataWithMobileType);
      } else {
        updatedFormPhoneData.push({ type: PhoneType.Mobile, phoneNumber: '' });
      }
    }

    if (firstPhoneNumDataWithHomeTypeAndNum) {
      updatedFormPhoneData.push(firstPhoneNumDataWithHomeTypeAndNum);
    } else if (firstPhoneNumDataWithHomeType) {
      updatedFormPhoneData.push(firstPhoneNumDataWithHomeType);
    }

    if (firstPhoneNumDataWithWorkTypeAndNum) {
      updatedFormPhoneData.push(firstPhoneNumDataWithWorkTypeAndNum);
    } else if (firstPhoneNumDataWithWorkType) {
      updatedFormPhoneData.push(firstPhoneNumDataWithWorkType);
    }

    setFieldValue(`${id}.phones`, []);

    updatedFormPhoneData.forEach((updatedPhoneData, index) => {
      setFieldValue(`${id}.phones[${index}]`, updatedPhoneData);
    });
  }, []);

  const [ phoneNumbersToDisplay, phoneNumberBtnsToDisplay ] = useMemo(() => {
    const phoneNumbersToDisplay: PhoneModel[] = [];
    const phoneNumberBtnsToDisplay: PhoneModel[] = [];

    phoneNumbersToDisplay.push(firstPhoneNumDataWithMobileTypeAndNum || firstPhoneNumDataWithMobileType || { type: PhoneType.Mobile, phoneNumber: '' });

    if (firstPhoneNumDataWithHomeTypeAndNum) {
      phoneNumbersToDisplay.push(firstPhoneNumDataWithHomeTypeAndNum);
    } else if (firstPhoneNumDataWithHomeType) {
      phoneNumbersToDisplay.push(firstPhoneNumDataWithHomeType);
    } else {
      phoneNumberBtnsToDisplay.push({ type: PhoneType.Home, phoneNumber: '' });
    }

    if (firstPhoneNumDataWithWorkTypeAndNum) {
      phoneNumbersToDisplay.push(firstPhoneNumDataWithWorkTypeAndNum);
    } else if (firstPhoneNumDataWithWorkType) {
      phoneNumbersToDisplay.push(firstPhoneNumDataWithWorkType);
    } else {
      phoneNumberBtnsToDisplay.push({ type: PhoneType.Work, phoneNumber: '' });
    }

    return [ phoneNumbersToDisplay, phoneNumberBtnsToDisplay ];
  }, [phoneDataFromForm]);

  return (
    <FieldArray
      name={`${prefix}phones`}
      render={(arrHelpers) => (
        <>
          {phoneNumbersToDisplay.map((phoneNumberToDisplay, index) => (
            <div key={`${prefix}phone${index}`}>
              <Field
                component={PhoneField}
                id={`${prefix}phones[${index}]`}
                defaultLabelId="phone"
                onRemove={phoneNumberToDisplay.type !== PhoneType.Mobile ? () => arrHelpers.remove(index) : undefined}
                backupPhoneData={phoneNumberToDisplay}
              />
            </div>
          ))}

          {phoneNumberBtnsToDisplay.map((phoneNumberBtnToDisplay) => (
            <div className="mb-3" key={`phoneNumberBtn-${phoneNumberBtnToDisplay.type}`}>
              <button
                onClick={() => {
                  arrHelpers.push({ type: phoneNumberBtnToDisplay.type, phoneNumber: '' });
                }}
                type="button"
              >
                <AddIcon />
                <strong>
                  <Text id="phones.addPhoneNumber" data={{ phoneType: phoneNumberBtnToDisplay.type.toLowerCase() }} />
                </strong>
              </button>
            </div>
          ))}
        </>
      )}
    />
  );
};
